/**
 * web-bluetooth.js — Web Bluetooth API 封装
 * 职责：使用浏览器标准 navigator.bluetooth 替代 APP 注入的 JS Bridge
 * 不再依赖 window.b2d / window.WebViewJavascriptBridge
 *
 * 限制说明：
 * 1. Web Bluetooth 仅支持 BLE（低功耗蓝牙）设备；经典蓝牙 SPP 设备无法连接
 * 2. requestDevice 必须在用户手势（如点击）上下文中调用
 * 3. 页面需通过 HTTPS 或 localhost 访问
 * 4. 设备 ID 为浏览器内部 ID，非真实 MAC 地址
 * 5. 不支持被动扫描列表，需通过系统弹框选择设备
 */

import { updateStore } from './platform'

let currentDevice = null
const knownDevices = new Map()

/** 检测当前浏览器是否支持 Web Bluetooth API */
export function isSupported() {
  return typeof navigator !== 'undefined' && !!navigator.bluetooth
}

/**
 * 获取 Web Bluetooth 支持的详细信息
 * 用于向用户展示精确的错误原因和解决方案
 */
export function getSupportDetail() {
  if (typeof navigator === 'undefined') {
    return {
      supported: false,
      reason: '当前环境不是浏览器，无法使用蓝牙功能'
    }
  }
  if (!navigator.bluetooth) {
    // Chrome/Edge 在 HTTP (非安全上下文) 下会隐藏 navigator.bluetooth
    if (typeof window !== 'undefined' && window.isSecureContext === false) {
      return {
        supported: false,
        reason: 'Chrome 浏览器需要在 HTTPS 或 localhost 环境下才能使用蓝牙功能。请通过 https:// 或 http://localhost 访问本页面。'
      }
    }
    const ua = navigator.userAgent
    const isChrome = /Chrome\/\d+/.test(ua) && !/Edg\//.test(ua) && !/OPR\//.test(ua)
    if (isChrome) {
      return {
        supported: false,
        reason: '当前 Chrome 浏览器未启用 Web Bluetooth，请检查是否为最新版本，并确保通过 HTTPS 访问。'
      }
    }
    return {
      supported: false,
      reason: '当前浏览器不支持 Web Bluetooth API，请使用 Chrome 79+、Edge 79+ 或 Android Chrome。'
    }
  }
  return { supported: true, reason: '' }
}

/** 获取当前已连接的设备 */
export function getCurrentDevice() {
  return currentDevice
}

/**
 * 请求用户选择一个蓝牙设备（弹出系统对话框）
 * @returns {Promise<{name, address, state, _raw}>}
 */
export async function requestDevice() {
  if (!isSupported()) {
    throw new Error('当前浏览器不支持 Web Bluetooth API')
  }
  const device = await navigator.bluetooth.requestDevice({
    acceptAllDevices: true,
    optionalServices: ['generic_access'] // TODO: 根据 MVCI 实际 BLE Service UUID 调整
  })

  const info = {
    name: device.name || '未知设备',
    address: device.id, // Web Bluetooth 使用内部 ID，非 MAC 地址
    state: 'disconnected',
    _raw: device
  }

  knownDevices.set(device.id, info)
  return info
}

/**
 * 连接指定蓝牙设备
 * @param {*} deviceInfo — 包含 address（即 device.id）的设备对象
 */
export async function connectDevice(deviceInfo) {
  const entry = knownDevices.get(deviceInfo.address)
  const raw = deviceInfo._raw || (entry && entry._raw)
  if (!raw) {
    throw new Error('无法找到设备对象，请重新搜索')
  }

  const server = await raw.gatt.connect()
  deviceInfo.state = 'connected'
  deviceInfo._server = server
  currentDevice = deviceInfo

  // 模拟 VIN 读取延迟
  setTimeout(() => {
    updateStore('updateVin', 'LSGGH59L9DS007027')
    updateStore('VINCompleteFlag', 1)
  }, 2000)

  return { code: '00', device: deviceInfo }
}

/** 断开当前连接的蓝牙设备 */
export async function disconnectDevice() {
  if (currentDevice) {
    const raw = currentDevice._raw
    if (raw && raw.gatt && raw.gatt.connected) {
      await raw.gatt.disconnect()
    }
    if (currentDevice._server && currentDevice._server.connected) {
      await currentDevice._server.disconnect()
    }
    currentDevice.state = 'disconnected'
    delete currentDevice._server
    currentDevice = null
  }
  return { code: '00' }
}

/** 打开/初始化蓝牙 */
export async function openBT() {
  if (!isSupported()) {
    throw new Error('当前浏览器不支持 Web Bluetooth API')
  }
  return { code: '00', msg: '蓝牙已就绪' }
}

/**
 * 返回已知设备列表
 * 注意：Web Bluetooth 不支持被动扫描，列表仅包含用户已选择过的设备
 */
export function listBTItems() {
  const items = Array.from(knownDevices.values()).map(d => ({
    name: d.name,
    address: d.address,
    state: d.state || 'disconnected'
  }))
  return {
    code: '00',
    result: '00',
    data: JSON.stringify(items),
    msg: 'OK'
  }
}

/** 监听蓝牙状态变化 — Web Bluetooth 暂不支持 */
export function watchBtstate(callback) {
  console.log('[WebBluetooth] 浏览器 API 不支持全局蓝牙状态监听')
}

/** 监听蓝牙连接状态 — Web Bluetooth 暂不支持 */
export function watchBTStatus(callback) {
  console.log('[WebBluetooth] 浏览器 API 不支持连接状态主动推送')
}

/** 监听 VIN 读取完成 — Web Bluetooth 暂不支持 */
export function watchVIN(callback) {
  console.log('[WebBluetooth] 浏览器 API 不支持 VIN 读取推送')
}
